import React, { useEffect, useState } from 'react'
import { api } from '../../lib/api'
import type { EventLog, Organization } from '../../lib/types'

const PAGE_SIZE = 50

export default function AdminEventLogs() {
  const [logs, setLogs] = useState<EventLog[]>([])
  const [orgs, setOrgs] = useState<Organization[]>([])
  const [eventType, setEventType] = useState('')
  const [orgFilter, setOrgFilter] = useState('')
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(true)

  useEffect(() => { api('/admin/organizations').then(setOrgs) }, [])

  async function load() {
    setLoading(true)
    const params = new URLSearchParams()
    if (eventType) params.set('event_type', eventType)
    if (orgFilter) params.set('organization_id', orgFilter)
    params.set('limit', String(PAGE_SIZE))
    params.set('offset', String(offset))
    setLogs(await api(`/admin/event-logs?${params.toString()}`))
    setLoading(false)
  }

  useEffect(() => { load() }, [eventType, orgFilter, offset])
  useEffect(() => { setOffset(0) }, [eventType, orgFilter])

  function orgName(id: string | null) {
    if (!id) return 'system'
    return orgs.find((o) => o.id === id)?.name || `org ${id.slice(0, 8)}`
  }

  return (
    <div>
      <div className="page-head">
        <h1>Event log</h1>
        <div className="ph-sub">The structured audit trail — also the foundation for future AI training data.</div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <div className="field" style={{ maxWidth: 280 }}>
          <label>Filter by event type</label>
          <input className="inp" placeholder="e.g. case_reached_session_4" value={eventType} onChange={(e) => setEventType(e.target.value)} />
        </div>
        <div className="field" style={{ maxWidth: 240 }}>
          <label>Filter by organization</label>
          <select className="inp" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
            <option value="">All organizations</option>
            {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="page-loading">Loading…</div>
      ) : (
        <>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Time</th><th>Event</th><th>Entity</th><th>Organization</th><th>Actor</th></tr></thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id}>
                    <td>{new Date(l.created_at).toLocaleString()}</td>
                    <td><span className="badge badge-blue">{l.event_type}</span></td>
                    <td>{l.entity_type}{l.entity_id ? ` · ${l.entity_id.slice(0, 8)}` : ''}</td>
                    <td>{orgName(l.organization_id)}</td>
                    <td title={l.actor_user_id || 'system'}>{l.actor_user_id ? `user ${l.actor_user_id.slice(0, 8)}` : 'system'}</td>
                  </tr>
                ))}
                {logs.length === 0 && <tr><td colSpan={5} className="muted">No events match this filter.</td></tr>}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
            <span className="muted" style={{ fontSize: 12.5 }}>
              Showing {logs.length === 0 ? 0 : offset + 1}–{offset + logs.length}
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn small outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>← Previous</button>
              <button className="btn small outline" disabled={logs.length < PAGE_SIZE} onClick={() => setOffset(offset + PAGE_SIZE)}>Next →</button>
            </div>
          </div>
        </>
      )}

      <p className="legal-note">
        Actor names aren't resolved here — there's no existing endpoint that can look up an arbitrary user
        across organizations, so actor is shown as a user ID rather than a name (see
        <code> DASHBOARD_INTEGRATION_AUDIT.md</code> category D). Organization names are resolved client-side
        from the organizations you already have access to.
      </p>
    </div>
  )
}
